import {
  Dispatch,
  SetStateAction,
  Suspense,
  useLayoutEffect,
  useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import {
  advancedEventsLayoutRelations,
  events,
  markets
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { createArray } from 'utils/helpers/create-fake-array';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import { MenuItem } from 'interfaces/generic';
import {
  EAdvancedEventsLayouts,
  TGameMainInfoType,
  TGameMarketsInfoType
} from 'interfaces/sportsbook';
import { Game } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { AdvancedMarketsFilter } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/__partials/AdvancedMarketsFilter';
import { EGroupGamesBy } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedMatchCardsDesktop';
import { ADVANCED_SKELETON_COUNTS } from 'newelements/Sportsbook/layouts/advanced/constants/skeleton-counts';
import { ADVANCED_LEFT_MENU_DATA_REDUX_KEY } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedLeftSideBar/AdvancedLeftMenu';
import { AdvancedMatchCardSkeleton } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard/Skeleton';
import { EmptyMatches } from 'components/EmptyResultBoxes/EmptyMatches';
import { MatchCardInlineSkeleton } from 'components/MatchCardInline/Skeleton';
import type { TRequestWhere } from 'newcomponents/Shared/sportsbook/FavoriteTeam/partials/GamesListFilterByCompetitions';
import { useMounted } from 'hooks/useMounted';
import {
  setAdvancedGamesMainInfo,
  setAdvancedGamesMarketsInfo,
  setAdvancedSelectedMarkets
} from 'store/actions/new-sportsbook';
import { advancedSelectedMarketsInitialState } from 'store/reducers/new-sportsbook';
import { TStreamData } from 'store/reducers/sport-data';
import {
  getAdvancedGamesMainInfo,
  getAdvancedGamesMarketsInfo,
  getAdvancedSelectedMarkets,
  getSwarmResponse
} from 'store/selectors/new-sportsbook';
import { getMarketTypes } from 'store/selectors/sport-data';

const { AdvancedMatchCardsDesktop } = lazy(
  () =>
    import(
      /* webpackChunkName: "advanced-match-card-desktop" */ 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedMatchCardsDesktop'
    )
);

const { AdvancedMatchCardsMobile } = lazy(
  () =>
    import(
      /* webpackChunkName: "advanced-match-card-mobile" */ 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedMatchCardsMobile'
    )
);

export type TAdvancedGameCardsWithMarketsFiltersProps = {
  gameIds: number[] | null;
  selectedSport: MenuItem | null | undefined;
  layoutType: EAdvancedEventsLayouts;
  selectedEventIds: string[];
  streamData: Array<[TStreamData]> | null;
  marketsCountsReduxKey?: string;
  skeletonCount?: number;
  setLoadMoreBtnLoading?: Dispatch<SetStateAction<boolean>>;
  groupGamesBy?: EGroupGamesBy;
  LoadMoreButton?: JSX.Element | null;
  addSportSectionInHeader?: boolean;
  showEmptyView?: boolean;
};

export const AdvancedGameCardsWithMarketsFilters = ({
  gameIds,
  selectedSport,
  layoutType,
  selectedEventIds,
  streamData,
  skeletonCount = ADVANCED_SKELETON_COUNTS._3,
  setLoadMoreBtnLoading,
  marketsCountsReduxKey,
  groupGamesBy,
  LoadMoreButton,
  addSportSectionInHeader,
  showEmptyView
}: TAdvancedGameCardsWithMarketsFiltersProps) => {
  const { mounted } = useMounted();
  const dispatch = useDispatch();

  const selectedMarketsReduxKey = selectedSport?.alias
    ? `${layoutType}-${selectedSport.alias}`
    : '';

  const advancedLeftSideBarSportsData =
    useSelector(getSwarmResponse)?.[ADVANCED_LEFT_MENU_DATA_REDUX_KEY];

  const gamesInfo = useSelector(getAdvancedGamesMainInfo)?.[layoutType];
  const gamesMarketsInfo = useSelector(getAdvancedGamesMarketsInfo)?.[
    layoutType
  ];

  let selectedMarkets = useSelector(getAdvancedSelectedMarkets)?.[
    selectedMarketsReduxKey
  ];

  if (SpringConfigs.MOCKED_DATA) {
    selectedMarkets = {
      first: 'P1XP2',
      second: 'P1XP2',
      third: 'P1XP2'
    };
  }

  const marketTypesBySport =
    useSelector(getMarketTypes)[selectedSport?.alias || ''];

  const gamesGeneralInfo = useMemo(() => {
    const someGameIdsInfoPresent =
      !!gamesInfo && gameIds?.some(id => !!gamesInfo[id]);

    if (
      advancedLeftSideBarSportsData &&
      someGameIdsInfoPresent &&
      selectedSport
    ) {
      const gamesObj: Record<string, TGameMainInfoType> = {};

      const getCurrentRegionObj = (regionAlias: string) => {
        if (advancedLeftSideBarSportsData[selectedSport.id]?.region) {
          return Object.values(
            advancedLeftSideBarSportsData[selectedSport.id].region || {}
          ).find(region => region.alias === regionAlias);
        }

        return null;
      };

      Object.values(gamesInfo).forEach(game => {
        const currentRegion =
          game.region_alias && getCurrentRegionObj(game.region_alias);

        if (currentRegion && currentRegion.competition) {
          const currentCompetition =
            game['_parent_id'] && currentRegion.competition[game['_parent_id']];

          if (currentCompetition) {
            const gameWithInfo: TGameMainInfoType = {
              ...game,
              sport_alias: selectedSport.alias,
              sportName: selectedSport.name,
              sportId: Number(selectedSport.id),
              regionAlias: currentRegion.alias,
              regionName: currentRegion.name,
              competitionName: currentCompetition.name,
              competitionId: currentCompetition.id
            };

            gamesObj[game.id] = gameWithInfo;
          }
        }
      });

      return gamesObj;
    }

    return null;
  }, [
    advancedLeftSideBarSportsData,
    gamesInfo,
    selectedSport,
    JSON.stringify(gameIds)
  ]);

  const selectedMarketTypeNames = useMemo(() => {
    if (marketTypesBySport && selectedMarkets) {
      return {
        first: marketTypesBySport.find(
          market => market.value === selectedMarkets.first
        )?.selectionTypesNames,
        second: marketTypesBySport.find(
          market => market.value === selectedMarkets.second
        )?.selectionTypesNames,
        third: marketTypesBySport.find(
          market => market.value === selectedMarkets.third
        )?.selectionTypesNames
      };
    }
  }, [selectedMarkets, marketTypesBySport]);

  useLayoutEffect(() => {
    if (selectedMarketsReduxKey && !selectedMarkets) {
      dispatch(
        setAdvancedSelectedMarkets({
          data: advancedSelectedMarketsInitialState,
          key: selectedMarketsReduxKey
        })
      );
    }
  }, [selectedMarketsReduxKey]);

  useLayoutEffect(() => {
    if (
      (!selectedSport?.alias || !gameIds?.length) &&
      !SpringConfigs.MOCKED_DATA
    ) {
      return;
    }

    const rId = RidGenerator.gForSubscribe(
      `ADVANCED_GET_${layoutType.toUpperCase()}_GAMES`
    );

    const what = {
      game: advancedEventsLayoutRelations.game
    };

    const where: TRequestWhere = {
      ...(gameIds && { game: { id: { '@in': gameIds } } })
    };

    if (selectedSport?.alias) {
      where.sport = { alias: selectedSport?.alias };
    }

    const onResponse = (data: { game: Record<string, Game> }) => {
      if (data && data.game && mounted.current) {
        dispatch(
          setAdvancedGamesMainInfo({ data: data.game, key: layoutType })
        );

        setLoadMoreBtnLoading?.(false);
      }
    };

    executeCommand(rId, what, where, onResponse, true, onResponse);

    return () => {
      unsubscribeCommand(rId);
    };
  }, [selectedSport?.alias, JSON.stringify(gameIds)]);

  useLayoutEffect(() => {
    if (
      !selectedMarkets ||
      selectedMarkets === advancedSelectedMarketsInitialState ||
      !gameIds?.length
    ) {
      return;
    }

    const rId = RidGenerator.gForSubscribe(
      `ADVANCED_GET_${layoutType.toUpperCase()}_GAMES_MARKETS`
    );

    const what = {
      game: [['id']],
      market: markets.market,
      event: events.event
    };

    const selectedMarketsValues =
      Object.values(selectedMarkets).filter(Boolean);

    const where: TRequestWhere = {
      game: { id: { '@in': gameIds } },
      ...(selectedMarketsValues.length && {
        market: { type: { '@in': [...Object.values(selectedMarkets)] } }
      })
    };

    if (selectedSport?.alias) {
      where.sport = { alias: selectedSport?.alias };
    }

    const onResponse = (data: {
      game: Record<string, { market: TGameMarketsInfoType }>;
    }) => {
      if (data && data.game && mounted.current) {
        dispatch(
          setAdvancedGamesMarketsInfo({ data: data.game, key: layoutType })
        );
      }
    };

    executeCommand(rId, what, where, onResponse, true, onResponse);

    return () => {
      unsubscribeCommand(rId);
    };
  }, [selectedMarkets, JSON.stringify(gameIds)]);

  return (
    <>
      <AdvancedMarketsFilter
        marketTypes={marketTypesBySport}
        selectedMarkets={selectedMarkets}
        selectedMarketsReduxKey={selectedMarketsReduxKey}
        selectedMarketTypeNames={selectedMarketTypeNames}
        selectedSport={selectedSport}
        addSportSectionInHeader={addSportSectionInHeader}
        doNotShowMarkerFiltersRow={showEmptyView}
      />

      {showEmptyView ? (
        <EmptyMatches />
      ) : isMobile() ? (
        <div
          className={cc([
            'advancedMobileGamesWrapper',
            { 'advancedMobileGamesWrapper--withCollapse': groupGamesBy }
          ])}
        >
          <Suspense
            fallback={createArray(skeletonCount).map(key => (
              <MatchCardInlineSkeleton key={key} />
            ))}
          >
            <AdvancedMatchCardsMobile
              selectedMarkets={selectedMarkets}
              gamesMarketsInfo={gamesMarketsInfo}
              isLive={layoutType === EAdvancedEventsLayouts.live}
              gamesGeneralInfo={gamesGeneralInfo}
              skeletonCount={skeletonCount}
              gameIds={gameIds}
              marketsCountsReduxKey={marketsCountsReduxKey}
              groupGamesBy={groupGamesBy}
            />
            {LoadMoreButton}
          </Suspense>
        </div>
      ) : (
        <Suspense
          fallback={createArray(skeletonCount).map(key => (
            <AdvancedMatchCardSkeleton
              key={key}
              isLive={layoutType === EAdvancedEventsLayouts.live}
            />
          ))}
        >
          <AdvancedMatchCardsDesktop
            selectedMarkets={selectedMarkets}
            selectedMarketTypeNames={selectedMarketTypeNames}
            gamesMarketsInfo={gamesMarketsInfo}
            isLive={layoutType === EAdvancedEventsLayouts.live}
            gamesGeneralInfo={gamesGeneralInfo}
            skeletonCount={skeletonCount}
            gameIds={gameIds}
            selectedEventIds={selectedEventIds}
            streamData={streamData}
            groupGamesBy={groupGamesBy}
          />
          {LoadMoreButton}
        </Suspense>
      )}
    </>
  );
};
